import { API_BASE_URL } from '../config/api'
import { getAuthHeaders } from '../utils/authUtils'
import { isHiddenDirectoryOrg } from '../utils/hiddenDirectoryOrgs'

export interface SupplyRequest {
  id: string
  title: string
  description: string
  estimatedWeight: string
  urgency: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  status: 'NEW' | 'ACCEPTED' | 'PICKED_UP' | 'DELIVERED' | 'CANCELLED' | 'EXPIRED'
  specialInstructions?: string
  contactName: string
  contactPhone: string
  pickupStreet: string
  pickupCity: string
  pickupState: string
  pickupZipCode: string
  pickupCountry: string
  pickupLatitude?: number
  pickupLongitude?: number
  deliveryStreet?: string
  deliveryCity?: string
  deliveryState?: string
  deliveryZipCode?: string
  deliveryCountry?: string
  deliveryLatitude?: number
  deliveryLongitude?: number
  scheduledDate?: string
  pickupTime?: string
  deliveryTime?: string
  completedDate?: string
  expiresAt?: string
  archivedAt?: string
  supplierId: string
  volunteerId?: string
  foodBankId?: string
  notes?: string
  createdAt: string
  updatedAt: string
  supplier: {
    id: string
    firstName: string
    lastName: string
    email?: string
    phone?: string
    userType: string
  }
  volunteer?: {
    id: string
    firstName: string
    lastName: string
  }
  items: SupplyRequestItem[]
}

export interface SupplyRequestItem {
  id: string
  name: string
  quantity: number
  unit: string
  category: string
  expirationDate: string
  isPerishable: boolean
  description?: string
}

export interface CreateSupplyRequestData {
  title: string
  description: string
  estimatedWeight: string
  urgency: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  specialInstructions?: string
  /** Optional on create — backend fills from food bank profile */
  contactName?: string
  contactPhone?: string
  pickupStreet?: string
  pickupCity?: string
  pickupState?: string
  pickupZipCode?: string
  pickupCountry?: string
  pickupLatitude?: number
  pickupLongitude?: number
  deliveryStreet?: string
  deliveryCity?: string
  deliveryState?: string
  deliveryZipCode?: string
  deliveryCountry?: string
  deliveryLatitude?: number
  deliveryLongitude?: number
  scheduledDate?: string
  pickupTime?: string
  expiresAt?: string
  items?: SupplyRequestItem[]
}

function combineDateAndTime(dateStr?: string, timeStr?: string): string | undefined {
  if (!dateStr || !timeStr) return undefined
  if (timeStr.includes('T')) return timeStr
  const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr
  return `${datePart}T${timeStr}`
}

// Get all supply requests with optional filters
export async function getSupplyRequests(filters?: {
  status?: string
  urgency?: string
  foodBankId?: string
}): Promise<SupplyRequest[]> {
  try {
    const queryParams = new URLSearchParams()
    if (filters?.status) queryParams.append('status', filters.status)
    if (filters?.urgency) queryParams.append('urgency', filters.urgency)
    if (filters?.foodBankId) queryParams.append('foodBankId', filters.foodBankId)

    const url = `${API_BASE_URL}/api/supply-requests${queryParams.toString() ? '?' + queryParams.toString() : ''}`
    const response = await fetch(url)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch supply requests')
    }

    return (data.data || []).filter((request: SupplyRequest) => !isHiddenDirectoryOrg(request))
  } catch (error) {
    console.error('Error fetching supply requests:', error)
    throw error
  }
}

// Get a specific supply request by ID
export async function getSupplyRequest(id: string): Promise<SupplyRequest | null> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/supply-requests/${id}`)
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch supply request')
    }

    if (!data.data || isHiddenDirectoryOrg(data.data)) {
      return null
    }

    return data.data
  } catch (error) {
    console.error('Error fetching supply request:', error)
    return null
  }
}

// Create a new supply request
export async function createSupplyRequest(supplyRequestData: CreateSupplyRequestData): Promise<SupplyRequest | null> {
  try {
    const normalizedPickupTime = combineDateAndTime(
      supplyRequestData.scheduledDate,
      supplyRequestData.pickupTime || (supplyRequestData as any).scheduledTime
    )

    const payload: CreateSupplyRequestData = {
      title: supplyRequestData.title,
      description: supplyRequestData.description,
      estimatedWeight: supplyRequestData.estimatedWeight,
      urgency: supplyRequestData.urgency,
      expiresAt: supplyRequestData.expiresAt || undefined,
      items: supplyRequestData.items,
      ...(normalizedPickupTime
        ? {
            scheduledDate: supplyRequestData.scheduledDate,
            pickupTime: normalizedPickupTime
          }
        : {})
    }

    const response = await fetch(`${API_BASE_URL}/api/supply-requests`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeaders()
      },
      body: JSON.stringify(payload)
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to create supply request')
    }

    return data.data
  } catch (error) {
    console.error('Error creating supply request:', error)
    throw error
  }
}

// Get supply requests created by current food bank
export async function getMyCreatedSupplyRequests(): Promise<SupplyRequest[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/supply-requests/my/created`, {
      headers: getAuthHeaders()
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch my supply requests')
    }

    return data.data || []
  } catch (error) {
    console.error('Error fetching my supply requests:', error)
    throw error
  }
}

export async function getMyArchivedSupplyRequests(): Promise<SupplyRequest[]> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/supply-requests/my/created?archived=true`, {
      headers: getAuthHeaders()
    })
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch archived supply requests')
    }

    return data.data || []
  } catch (error) {
    console.error('Error fetching archived supply requests:', error)
    throw error
  }
}

export async function archiveSupplyRequest(id: string): Promise<SupplyRequest | null> {
  const response = await fetch(`${API_BASE_URL}/api/supply-requests/${id}/archive`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    }
  })
  const data = await response.json()
  if (!response.ok) throw new Error(data.error || 'Failed to archive supply request')
  return data.data
}

// Legacy function for backward compatibility - maps to new supply requests API
export async function createPickupRequest(pickupData: CreateSupplyRequestData): Promise<SupplyRequest | null> {
  return createSupplyRequest(pickupData)
}
